'use client';

import { Badge } from '@wilkins-software/universal-components';
import { Button } from '@wilkins-software/universal-components';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@wilkins-software/universal-components';
import { ContactCtaLink } from './contact/contact-cta-link.component';
import {
  Check,
  Code,
  Smartphone,
  Globe,
  ArrowRight,
  Zap,
  Shield,
  Headphones,
  Server,
  Mail,
  Settings,
  Database,
} from 'react-feather';
import { PrimaryNavigationClient } from '../components/primaryNavigation/PrimaryNavigationClient';
import { Footer } from '../components/footer';
import { SOCIALS } from '../constants';

export default function TechnicalProjectPricingClient() {
  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigationClient isHeroImagePage={false} />
      <main>
        {/* Hero Section */}
        <section className="py-20 pt-32">
          <div className="homepage-wrapper text-center">
            <Badge
              variant="secondary"
              className="mb-6 bg-green-light text-primary border-primary/20"
            >
              <Code className="w-3 h-3 mr-1" />
              Development & IT Services
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-balance mb-6">
              Complete Digital
              <span className="text-primary block">Solutions</span>
            </h1>
            <p className="text-xl text-muted-foreground text-balance mb-8 max-w-2xl mx-auto">
              From custom websites to mobile apps, plus all the IT services you
              need to run your business online.
            </p>
          </div>
        </section>

        {/* Development Services */}
        <section className="py-16">
          <div className="homepage-wrapper">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Development Services</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Custom-built solutions tailored to your business needs. Each
                project includes discovery, design, development, and deployment.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {/* Business Websites */}
              <Card className="border-border/50 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Business Websites</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1 mb-2">
                    <span className="text-3xl font-bold">$5,000</span>
                    <span className="text-sm text-muted-foreground">
                      - $15,000
                    </span>
                  </div>
                  <CardDescription>
                    Professional websites with CMS, SEO optimization, and mobile
                    responsiveness.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">5-15 custom pages</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Content management system</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      SEO & performance optimization
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Contact forms & integrations
                    </span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-transparent"
                    variant="outline"
                    asChild
                  >
                    <ContactCtaLink topicId="quote-business-website">
                      Get Website Quote
                    </ContactCtaLink>
                  </Button>
                </CardFooter>
              </Card>

              {/* E-commerce */}
              <Card className="border-border/50 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Database className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">E-commerce Stores</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1 mb-2">
                    <span className="text-3xl font-bold">$8,000</span>
                    <span className="text-sm text-muted-foreground">
                      - $25,000
                    </span>
                  </div>
                  <CardDescription>
                    Online stores with payment processing, inventory management,
                    and order fulfillment.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Product catalog & search</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Payment processing (Stripe, PayPal)
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Inventory & order management
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Customer accounts & reviews</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-transparent"
                    variant="outline"
                    asChild
                  >
                    <ContactCtaLink topicId="quote-ecommerce">
                      Get Store Quote
                    </ContactCtaLink>
                  </Button>
                </CardFooter>
              </Card>

              {/* Web Applications */}
              <Card className="border-border/50 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Web Applications</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1 mb-2">
                    <span className="text-3xl font-bold">$15,000</span>
                    <span className="text-sm text-muted-foreground">
                      - $50,000
                    </span>
                  </div>
                  <CardDescription>
                    Custom dashboards, user portals, and business management
                    systems.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">User authentication & roles</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Custom dashboards & reporting
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Database integration & APIs</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Third-party integrations</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-transparent"
                    variant="outline"
                    asChild
                  >
                    <ContactCtaLink topicId="quote-web-application">
                      Get App Quote
                    </ContactCtaLink>
                  </Button>
                </CardFooter>
              </Card>

              {/* Mobile Apps */}
              <Card className="border-border/50 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Mobile Apps</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1 mb-2">
                    <span className="text-3xl font-bold">$30,000</span>
                    <span className="text-sm text-muted-foreground">
                      - $100,000
                    </span>
                  </div>
                  <CardDescription>
                    Native iOS and Android apps with offline functionality and
                    push notifications.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">iOS & Android development</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Push notifications & offline mode
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">App Store deployment</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Backend API & cloud sync</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-transparent"
                    variant="outline"
                    asChild
                  >
                    <ContactCtaLink topicId="quote-mobile-app">
                      Get Mobile App Quote
                    </ContactCtaLink>
                  </Button>
                </CardFooter>
              </Card>

              {/* Enterprise Solutions */}
              <Card className="border-border/50 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">
                      Enterprise Solutions
                    </CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1 mb-2">
                    <span className="text-3xl font-bold">$100,000</span>
                    <span className="text-sm text-muted-foreground">+</span>
                  </div>
                  <CardDescription>
                    Large-scale systems with advanced security, integrations,
                    and compliance requirements.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Enterprise security & compliance
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Complex system integrations</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Dedicated project team</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">SLA & ongoing support</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-transparent"
                    variant="outline"
                    asChild
                  >
                    <ContactCtaLink topicId="quote-enterprise">
                      Discuss Project
                    </ContactCtaLink>
                  </Button>
                </CardFooter>
              </Card>

              {/* Custom Development */}
              <Card className="border-border/50 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Code className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">
                      Custom Development
                    </CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1 mb-2">
                    <span className="text-3xl font-bold">Quote</span>
                    <span className="text-sm text-muted-foreground">Based</span>
                  </div>
                  <CardDescription>
                    Unique solutions that don't fit standard categories. We
                    build exactly what you need.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Custom requirements analysis
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Flexible technology stack</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Agile development process</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Ongoing consultation</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-transparent"
                    variant="outline"
                    asChild
                  >
                    <ContactCtaLink topicId="quote-custom-development">
                      Start Discussion
                    </ContactCtaLink>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </section>

        {/* IT Services */}
        <section className="py-16 bg-muted/30">
          <div className="homepage-wrapper">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">IT Services & Support</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Keep your business running smoothly with our comprehensive IT
                services and ongoing support.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Web Hosting */}
              <Card className="border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Server className="w-4 h-4 text-primary" />
                    </div>
                    <CardTitle className="text-lg">Web Hosting</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-2xl font-bold">$25</span>
                    <span className="text-sm text-muted-foreground">
                      /month
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">99.9% uptime guarantee</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">SSL certificates included</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Daily backups</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      CDN & performance optimization
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Email Services */}
              <Card className="border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Mail className="w-4 h-4 text-primary" />
                    </div>
                    <CardTitle className="text-lg">Business Email</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-2xl font-bold">$8</span>
                    <span className="text-sm text-muted-foreground">
                      /user/month
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Professional email addresses
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">50GB storage per user</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Calendar & contacts sync</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Mobile & desktop apps</span>
                  </div>
                </CardContent>
              </Card>

              {/* Domain Management */}
              <Card className="border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Globe className="w-4 h-4 text-primary" />
                    </div>
                    <CardTitle className="text-lg">Domain Services</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-2xl font-bold">$15</span>
                    <span className="text-sm text-muted-foreground">
                      - $200+/year
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Domain registration (.com, .org, etc.)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">DNS management</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Domain privacy protection</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Subdomain setup</span>
                  </div>
                </CardContent>
              </Card>

              {/* Maintenance & Support */}
              <Card className="border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Settings className="w-4 h-4 text-primary" />
                    </div>
                    <CardTitle className="text-lg">Maintenance</CardTitle>
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-2xl font-bold">$150</span>
                    <span className="text-sm text-muted-foreground">
                      /month
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Security updates & patches</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Content updates (2 hours/month)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Performance monitoring</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-primary flex-shrink-0" />
                    <span className="text-sm">Priority email support</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-8">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90"
                asChild
              >
                <ContactCtaLink topicId="quote-it-services">
                  Get IT Services Quote
                </ContactCtaLink>
              </Button>
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-16">
          <div className="homepage-wrapper">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Choose Wilkins Software?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  Complete Solutions
                </h3>
                <p className="text-muted-foreground">
                  From development to hosting to ongoing support - we handle
                  everything so you can focus on your business.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  Transparent Pricing
                </h3>
                <p className="text-muted-foreground">
                  No hidden fees or surprise costs. Clear pricing for each
                  service with detailed quotes upfront.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Headphones className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  Long-term Partnership
                </h3>
                <p className="text-muted-foreground">
                  We're here for the long haul - providing ongoing support,
                  updates, and growth as your business evolves.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="homepage-wrapper">
            <h2 className="text-2xl font-bold text-center mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">
                    Do you offer package deals for multiple services?
                  </h3>
                  <p className="text-muted-foreground">
                    Yes! We offer bundled pricing when you combine development
                    with hosting, email, and maintenance services. Contact us
                    for a custom quote that can save you 15-25% compared to
                    individual services.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">
                    What's included in the development pricing?
                  </h3>
                  <p className="text-muted-foreground">
                    All development projects include discovery, design,
                    development, testing, deployment, and basic training.
                    Hosting setup and first month of support are included at no
                    extra charge.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">
                    Can I switch hosting providers later?
                  </h3>
                  <p className="text-muted-foreground">
                    Absolutely. We build all projects to be portable and provide
                    migration assistance if you ever need to move to a different
                    hosting provider. Your code and data always belong to you.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">
                    Do you provide emergency support?
                  </h3>
                  <p className="text-muted-foreground">
                    Yes, we offer 24/7 emergency support for critical issues at
                    $200/hour. Maintenance plan customers get priority response
                    and reduced emergency rates.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="homepage-wrapper text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              From a focused website to a larger custom build, we can help you
              ship it online.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90"
                asChild
              >
                <ContactCtaLink topicId="free-consultation">
                  Schedule Free Consultation
                </ContactCtaLink>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="/#services">View Our Portfolio</a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer
        socialLinks={SOCIALS}
        copyrightText="© 2025 Wilkins Software · Custom software, platforms, and technical delivery."
      />
    </div>
  );
}
